import React, { useEffect, useMemo, useRef, useState } from "react";
import { SESSION_STATUSES } from "../constants";
import SessionNoteTooltip from "./SessionNoteTooltip";
import ReactDOM from "react-dom";
import { FaCheckCircle } from "react-icons/fa";
import { FaTriangleExclamation } from "react-icons/fa6";
import { IoMdNotificationsOutline } from "react-icons/io";
import { PiNotepadBold } from "react-icons/pi";

function statusKey(status) {
  const value = (status || "Pending").toLowerCase().trim();
  if (value === "confirmed") return "confirmed";
  if (value === "present") return "present";
  if (value === "cancelled" || value === "canceled") return "cancelled";
  if (value === "no show" || value === "noshow" || value === "no_show" || value === "no-show") return "no-show";
  return "pending";
}

function formatTimeRange(start, end) {
  if (!start || !end) return "";
  const startDate = new Date(start);
  const endDate = new Date(end);
  const formatOpts = { hour: "numeric", minute: "2-digit" };
  return `${startDate.toLocaleTimeString([], formatOpts)} - ${endDate.toLocaleTimeString([], formatOpts)}`;
}

function resolveStudentLabel(session, studentLabel) {
  if (studentLabel) return studentLabel;
  if (session?.studentName && session?.studentGrade != null) {
    return `${session.studentName} (${session.studentGrade})`;
  }
  return session?.studentName || "Student";
}

export default function SessionCard({
  session,
  studentLabel,
  isSelected,
  onClick,
  onDoubleClick,
  onChangeStatus,
  onDragStart,
  isLocked,
  readOnly,
  notifications = [],
  showSessionLogIndicator = false,
  showMissingSessionLogIndicator = false,
  hasSessionLog = false,
  isPastSession = false,
  onOpenSessionLog,
  sessionLogIndicatorInteractive = true,
  sessionLogSubmittedTooltip = "Session log submitted",
  sessionLogMissingTooltip = "Session log missing",
  animationPhase = "",
}) {
  const [localStatus, setLocalStatus] = useState(session?.status || "Pending");
  const [isHovered, setIsHovered] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [conferenceDetail, setConferenceDetail] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    setLocalStatus(session?.status || "Pending");
  }, [session?.status]);

  useEffect(() => {
    if (!menuPos) return undefined;

    const handleClose = (event) => {
      if (event && typeof event.button === "number" && event.button === 2) return;
      setMenuPos(null);
    };

    document.addEventListener("mousedown", handleClose);
    document.addEventListener("click", handleClose);
    window.addEventListener("scroll", handleClose, true);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      document.removeEventListener("click", handleClose);
      window.removeEventListener("scroll", handleClose, true);
    };
  }, [menuPos]);

  useEffect(() => {
    const handleOtherMenuOpen = (event) => {
      const openedSessionId = event?.detail?.id;
      if (openedSessionId && openedSessionId !== session?.id) {
        setMenuPos(null);
      }
    };

    window.addEventListener("schedule-status-menu-open", handleOtherMenuOpen);
    return () => window.removeEventListener("schedule-status-menu-open", handleOtherMenuOpen);
  }, [session?.id]);

  const statusClass = useMemo(() => `sessionCard--status-${statusKey(localStatus)}`, [localStatus]);
  const hasNote = !!session?.note && session.note.trim().length > 0;

  const cardClasses = [
    "sessionCard",
    "sessionCard--timeline",
    statusClass,
    animationPhase ? `sessionCard--anim-${animationPhase}` : "",
    isSelected ? "sessionCardSelected" : "",
    hasNote ? "sessionCard--hasNote" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const canChangeStatus = typeof onChangeStatus === "function" && !isLocked && !readOnly;
  const activeNotifications = Array.isArray(notifications) ? notifications : [];
  const notificationCount = activeNotifications.length;
  const showNotificationIcon = notificationCount > 0;
  const showLogIndicator = !!showSessionLogIndicator && !!hasSessionLog;
  const showMissingLogIndicator =
    !!showSessionLogIndicator &&
    !!showMissingSessionLogIndicator &&
    !hasSessionLog &&
    !!isPastSession;
  const logAriaLabel = sessionLogIndicatorInteractive ? "Open session log" : sessionLogSubmittedTooltip;
  const missingLogAriaLabel = sessionLogIndicatorInteractive ? "Open missing session log" : sessionLogMissingTooltip;

  const handleLogIndicatorClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!sessionLogIndicatorInteractive) return;
    onOpenSessionLog?.(session);
  };

  const openConferenceDetail = (notificationItem) => {
    if (!notificationItem?.noteHtml) return;
    setConferenceDetail(notificationItem);
  };

  const closeNotificationModal = () => setNotificationOpen(false);
  const closeConferenceDetail = () => setConferenceDetail(null);
  const swallowOverlayEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleOverlayMouseDown = (event, closeHandler) => {
    swallowOverlayEvent(event);
    if (event.target === event.currentTarget) {
      closeHandler();
    }
  };

  const clampMenuPosition = (x, y) => {
    const margin = 12;
    const menuWidth = 180;
    const menuHeight = Math.max(180, (SESSION_STATUSES.length + 1) * 31);
    const maxX = Math.max(margin, window.innerWidth - menuWidth - margin);
    const maxY = Math.max(margin, window.innerHeight - menuHeight - margin);

    return {
      x: Math.min(Math.max(x, margin), maxX),
      y: Math.min(Math.max(y, margin), maxY),
    };
  };

  return (
    <>
      <div
        ref={cardRef}
        className={cardClasses}
        draggable={!isLocked && !readOnly}
        onDragStart={(event) => onDragStart?.(event, session)}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.(session);
        }}
        onDoubleClick={(event) => {
          event.stopPropagation();
          onDoubleClick?.(session);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!canChangeStatus) return;
          setMenuPos(null);
          const clamped = clampMenuPosition(event.clientX + 8, event.clientY + 8);
          setMenuPos(clamped);
          window.dispatchEvent(new CustomEvent("schedule-status-menu-open", { detail: { id: session?.id || null } }));
        }}
        title={hasNote ? "Has note" : "Right-click to change status"}
      >
        {showNotificationIcon ? (
          <button
            type="button"
            className="sessionCardIcon sessionCardIcon--notifications"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setNotificationOpen(true);
            }}
            title={`${notificationCount} notification${notificationCount === 1 ? "" : "s"}`}
            aria-label="Open notifications"
          >
            <IoMdNotificationsOutline size={12} />
            <span className="sessionCardIcon__badge" aria-hidden="true">
              {notificationCount > 9 ? "9+" : notificationCount}
            </span>
          </button>
        ) : null}

        {showLogIndicator ? (
          <button
            type="button"
            className={`sessionCardIcon sessionCardIcon--log ${sessionLogIndicatorInteractive ? "" : "sessionCardIcon--static"}`.trim()}
            onClick={handleLogIndicatorClick}
            title={sessionLogSubmittedTooltip}
            aria-label={logAriaLabel}
          >
            <FaCheckCircle size={11} />
          </button>
        ) : null}

        {showMissingLogIndicator ? (
          <button
            type="button"
            className={`sessionCardIcon sessionCardIcon--logMissing ${sessionLogIndicatorInteractive ? "" : "sessionCardIcon--static"}`.trim()}
            onClick={handleLogIndicatorClick}
            title={sessionLogMissingTooltip}
            aria-label={missingLogAriaLabel}
          >
            <FaTriangleExclamation size={11} />
          </button>
        ) : null}

        <div className="sessionStudent">{resolveStudentLabel(session, studentLabel)}</div>
        <div className="sessionSubject">{session?.subject || "No subject"}</div>
        <div className="sessionTime">{formatTimeRange(session?.start, session?.end)}</div>
      </div>

      {menuPos && canChangeStatus && ReactDOM.createPortal(
        <div
          className="sessionStatusMenu"
          style={{ top: menuPos.y, left: menuPos.x, zIndex: 999999 }}
          onClick={(event) => event.stopPropagation()}
        >
          {SESSION_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              className={`sessionStatusMenuItem ${statusKey(status) === statusKey(localStatus) ? "sessionStatusMenuItem--active" : ""}`}
              onClick={async () => {
                const previous = localStatus;
                setLocalStatus(status);
                try {
                  const ok = await onChangeStatus?.(status);
                  if (ok === false) {
                    setLocalStatus(previous);
                    return;
                  }
                  setMenuPos(null);
                } catch {
                  setLocalStatus(previous);
                }
              }}
            >
              {status}
            </button>
          ))}
        </div>,
        document.body
      )}

      {hasNote && (
        <SessionNoteTooltip
          note={session.note}
          isVisible={isHovered}
          anchorRect={cardRef.current?.getBoundingClientRect()}
        />
      )}

      {notificationOpen && ReactDOM.createPortal(
        <div
          className="sessionConferenceOverlay"
          onMouseDown={(event) => handleOverlayMouseDown(event, closeNotificationModal)}
          onClick={swallowOverlayEvent}
          onDoubleClick={swallowOverlayEvent}
        >
          <div
            className="sessionNotificationModal"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
          >
            <div className="sessionConferenceModal__header">
              <div className="sessionConferenceModal__title">
                <IoMdNotificationsOutline size={14} />
                <span>Notifications</span>
              </div>
              <button
                type="button"
                className="sessionConferenceModal__close"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  closeNotificationModal();
                }}
              >
                Close
              </button>
            </div>

            {notificationCount === 0 ? (
              <div className="sessionNotificationModal__empty">No notifications.</div>
            ) : (
              <div className="sessionNotificationModal__list">
                {activeNotifications.map((item, index) => {
                  const key = `${item?.id || item?.type || "notification"}-${index}`;
                  const isConference = item?.type === "conference";
                  return (
                    <div key={key} className="sessionNotificationItem">
                      <div className="sessionNotificationItem__header">
                        <span className={`sessionNotificationItem__pill sessionNotificationItem__pill--${item?.type || "default"}`}>
                          {isConference
                            ? "Conference"
                            : item?.type === "diagnostic"
                              ? "Diagnostic"
                              : item?.type === "first_session"
                                ? "First Session"
                                : "Notice"}
                        </span>
                        <span className="sessionNotificationItem__title">{item?.title || "Notification"}</span>
                      </div>
                      {item?.subtitle ? (
                        <div className="sessionNotificationItem__subtitle">{item.subtitle}</div>
                      ) : null}
                      {isConference && item?.noteHtml ? (
                        <button
                          type="button"
                          className="sessionNotificationItem__action"
                          onClick={() => openConferenceDetail(item)}
                        >
                          View Note
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {conferenceDetail && ReactDOM.createPortal(
        <div
          className="sessionConferenceOverlay"
          onMouseDown={(event) => handleOverlayMouseDown(event, closeConferenceDetail)}
          onClick={swallowOverlayEvent}
          onDoubleClick={swallowOverlayEvent}
        >
          <div
            className="sessionConferenceModal"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
          >
            <div className="sessionConferenceModal__header">
              <div className="sessionConferenceModal__title">
                <PiNotepadBold size={13} />
                <span>Conference Note</span>
              </div>
              <button
                type="button"
                className="sessionConferenceModal__close"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  closeConferenceDetail();
                }}
              >
                Close
              </button>
            </div>
            <div className="sessionConferenceModal__date">
              {conferenceDetail?.dateLabel ? `Date: ${conferenceDetail.dateLabel}` : "Date: -"}
            </div>
            <div
              className="sessionConferenceModal__content"
              dangerouslySetInnerHTML={{ __html: conferenceDetail?.noteHtml || "" }}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
